import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import RegionalService from '../services/RegionalService';
import UsuarioService from '../services/UsuarioService';
import { useAuth } from '../contexts/AuthContext';
import regionalApiService from '../services/RegionalApiService';
import ActiveRegionalBadge from './common/ActiveRegionalBadge';
import LoadingSpinner from './common/LoadingSpinner';
import { isAdminArea, isAdminRegional } from '../utils/adminMenu';
import ProcesosMenu from './common/ProcesosMenu';
import './AdminSelectRegional.css';

const AdminSelectRegional = () => {
  const navigate = useNavigate();
  const { updateToken, user, logout } = useAuth();
  const [regionales, setRegionales] = useState([]);
  const [viewMode, setViewMode] = useState('cards');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [actionMessage, setActionMessage] = useState({ type: '', text: '' });
  const [searchTerm, setSearchTerm] = useState('');
  const [ffModal, setFfModal] = useState({ open: false, regional: null, items: [], loading: false, error: '' });
  const [confirmModal, setConfirmModal] = useState({ open: false, regional: null, nextActivo: true, loading: false });
  const [isSelectingRegional, setIsSelectingRegional] = useState(false);
  const [selectingRegionalName, setSelectingRegionalName] = useState('');
  const [consumosModal, setConsumosModal] = useState({
    open: false,
    regional: null,
    loading: false,
    error: '',
    data: null
  });
  const isRegionalAdmin = isAdminRegional(user);
  const isAreaAdmin = isAdminArea(user);
  const isGeneralAdmin = !isRegionalAdmin && !isAreaAdmin;

  const filteredRegionales = useMemo(() => {
    if (!searchTerm.trim()) return regionales;
    const term = searchTerm.toLowerCase();
    return regionales.filter((reg) => {
      const name = (reg.nombre || reg.name || '').toLowerCase();
      const desc = (reg.descripcion || reg.detalle || '').toLowerCase();
      return name.includes(term) || desc.includes(term);
    });
  }, [regionales, searchTerm]);

  useEffect(() => {
    if (isAreaAdmin) {
      navigate('/admin-dashboard', { replace: true });
    }
  }, [isAreaAdmin, navigate]);

  useEffect(() => {
    const fetchRegionales = async () => {
      setIsLoading(true);
      setError('');
      setActionMessage({ type: '', text: '' });
      try {
      const response = isRegionalAdmin
        ? await regionalApiService.getMisRegionales()
        : await regionalApiService.getRegionales();
      const list = response.regionales || response.Regionales || response.data || response || [];
      const normalized = Array.isArray(list) ? list : [];
      setRegionales(normalized);
      RegionalService.setRegionalData({ regionales: normalized, areas: [], materias: [] });
      } catch (err) {
        console.error('❌ Error cargando regionales:', err);
        setError(err.message || 'No pudimos obtener los regionales.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchRegionales();
  }, [isRegionalAdmin]);

  const handleToggleRegionalActivo = async (regional) => {
    const regionalId = regional?.id || regional?.Id;
    if (!regionalId) return;
    const actualActivo = Boolean(regional?.activo ?? regional?.Activo ?? true);
    const nextActivo = !actualActivo;
    setConfirmModal({ open: true, regional, nextActivo, loading: false });
  };

  const closeConfirmModal = () => {
    if (confirmModal.loading) return;
    setConfirmModal({ open: false, regional: null, nextActivo: true, loading: false });
  };

  const confirmToggleRegional = async () => {
    const regionalId = confirmModal.regional?.id || confirmModal.regional?.Id;
    if (!regionalId) return;
    const nextActivo = confirmModal.nextActivo;
    setConfirmModal((prev) => ({ ...prev, loading: true }));
    setActionMessage({ type: '', text: '' });

    try {
      await regionalApiService.updateRegionalActivo(regionalId, nextActivo);
      setRegionales((prev) =>
        prev.map((item) => {
          const itemId = item?.id || item?.Id;
          if (itemId === regionalId) {
            return { ...item, Activo: nextActivo, activo: nextActivo };
          }
          return item;
        })
      );
      setActionMessage({
        type: 'success',
        text: nextActivo
          ? 'El regional fue activado correctamente.'
          : 'El regional fue desactivado correctamente.'
      });
      setConfirmModal({ open: false, regional: null, nextActivo: true, loading: false });
    } catch (err) {
      setConfirmModal((prev) => ({ ...prev, loading: false }));
      setActionMessage({
        type: 'error',
        text: err?.message || 'No pudimos actualizar el estado del regional.'
      });
    }
  };

  const handleSelect = (regional) => {
    const regionalId = regional?.id || regional?.Id;
    const regionalName = regional?.nombre || regional?.name || regional?.Nombre || 'regional';
    setSelectingRegionalName(regionalName);
    setIsSelectingRegional(true);
    RegionalService.setActiveRegional(regional);
    if (!regionalId) {
      navigate('/admin-dashboard', { replace: true });
      return;
    }

    const fetchFeatureFlags = async () => {
      try {
        const featureResponse = await regionalApiService.getFeatureFlagsByRegional(regionalId);
        const flags = featureResponse.featureFlags || featureResponse.FeatureFlags || [];
        RegionalService.setFeatureFlags(flags);
      } catch (flagError) {
        console.warn('No se pudieron cargar las feature flags del regional:', flagError);
        RegionalService.setFeatureFlags([]);
      }
    };

    fetchFeatureFlags()
      .finally(() => {
        UsuarioService.selectRegional(regionalId)
          .then((res) => {
            const token = res.token || res.Token;
            if (token) {
              updateToken(token);
            }
            navigate('/admin-dashboard', { replace: true });
          })
          .catch(() => {
            navigate('/admin-dashboard', { replace: true });
          });
      });
  };

  const openFeatureFlags = async (regional) => {
    const regionalId = regional?.id || regional?.Id;
    if (!regionalId) return;
    setFfModal({ open: true, regional, items: [], loading: true, error: '' });
    try {
      const featureResponse = await regionalApiService.getFeatureFlagsByRegional(regionalId);
      const items = featureResponse.featureFlags || featureResponse.FeatureFlags || [];
      setFfModal({ open: true, regional, items: Array.isArray(items) ? items : [], loading: false, error: '' });
    } catch (err) {
      setFfModal({
        open: true,
        regional,
        items: [],
        loading: false,
        error: err?.message || 'No se pudieron cargar las feature flags.'
      });
    }
  };

  const closeFeatureFlags = () => {
    setFfModal({ open: false, regional: null, items: [], loading: false, error: '' });
  };

  const openConsumos = async (regional) => {
    const regionalId = regional?.id || regional?.Id;
    if (!regionalId) return;
    setConsumosModal({ open: true, regional, loading: true, error: '', data: null });
    try {
      const response = await regionalApiService.getConsumosByRegional(regionalId);
      if (response && typeof response.ok !== 'undefined' && !response.ok) {
        throw new Error(response.error || 'No se pudieron cargar los consumos.');
      }
      const data = {
        year: response?.year ?? response?.Year,
        mesActual: response?.mesActual ?? response?.MesActual,
        meses: response?.meses ?? response?.Meses,
        maxTokens: response?.maxTokens ?? response?.MaxTokens
      };
      setConsumosModal({ open: true, regional, loading: false, error: '', data });
    } catch (err) {
      setConsumosModal({
        open: true,
        regional,
        loading: false,
        error: err?.message || 'No se pudieron cargar los consumos.',
        data: null
      });
    }
  };

  const closeConsumos = () => {
    setConsumosModal({ open: false, regional: null, loading: false, error: '', data: null });
  };

  const handleToggleFeatureFlag = async (flag) => {
    const regionalId = ffModal.regional?.id || ffModal.regional?.Id;
    if (!regionalId) return;
    const nombre = flag?.nombre || flag?.Nombre;
    if (!nombre) return;

    const nextActivo = !(flag?.activo ?? flag?.Activo);
    setFfModal((prev) => ({
      ...prev,
      items: prev.items.map((item) => {
        const itemNombre = item?.nombre || item?.Nombre;
        if (itemNombre === nombre) {
          return { ...item, Activo: nextActivo, activo: nextActivo };
        }
        return item;
      }),
      error: ''
    }));

    try {
      await regionalApiService.updateFeatureFlagByRegional(regionalId, nombre, nextActivo);
    } catch (err) {
      setFfModal((prev) => ({
        ...prev,
        items: prev.items.map((item) => {
          const itemNombre = item?.nombre || item?.Nombre;
          if (itemNombre === nombre) {
            return { ...item, Activo: !nextActivo, activo: !nextActivo };
          }
          return item;
        }),
        error: err?.message || 'No se pudo actualizar el feature flag.'
      }));
    }
  };

  const renderCards = () => (
    <div className="admin-regional-grid">
      {filteredRegionales.map((reg) => {
        const regionalId = reg.id || reg.Id;
        const activo = Boolean(reg?.activo ?? reg?.Activo ?? true);
        return (
          <div
            key={regionalId || reg.nombre}
            className={`regional-card${activo ? '' : ' regional-card-inactive'}`}
          >
            <div className="card-head">
              <div className="badge">Regional</div>
              <i className="fas fa-map-marker-alt" />
            </div>
            <h3>{reg.nombre || reg.name || 'Sin nombre'}</h3>
            {reg.descripcion && <p className="desc">{reg.descripcion}</p>}
            {reg.detalle && <p className="desc">{reg.detalle}</p>}
            <div className="card-footer">
              <button
                type="button"
                className="card-action primary"
                onClick={() => handleSelect(reg)}
                title="Administrar"
              >
                Administrar
                <i className="fas fa-arrow-right" />
              </button>
              <button
                type="button"
                className="card-action"
                onClick={() => regionalId && navigate(`/admin/regionales/${regionalId}/admins/nuevo`)}
                disabled={!regionalId}
                title="Administradores"
              >
                <i className="fas fa-user-plus" />
              </button>
              {isGeneralAdmin && (
                <button
                  type="button"
                  className="card-action"
                  onClick={() => openFeatureFlags(reg)}
                  disabled={!regionalId}
                  title="Feature flags"
                >
                  <i className="fas fa-toggle-on" />
                </button>
              )}
              {isGeneralAdmin && (
                <button
                  type="button"
                  className="card-action"
                  onClick={() => openConsumos(reg)}
                  disabled={!regionalId}
                  title="Consumos"
                >
                  <i className="fas fa-chart-bar" />
                </button>
              )}
              {isGeneralAdmin && (
                <button
                  type="button"
                  className={`card-action ${activo ? 'danger' : 'success'}`}
                  onClick={() => handleToggleRegionalActivo(reg)}
                  disabled={!regionalId}
                  title={activo ? 'Desactivar regional' : 'Activar regional'}
                >
                  <i className={`fas ${activo ? 'fa-ban' : 'fa-check'}`} />
                </button>
              )}
              {isGeneralAdmin && (
                <button
                  type="button"
                  className="card-action"
                  onClick={() => regionalId && navigate(`/admin/regionales/${regionalId}/editar`)}
                  disabled={!regionalId}
                  title="Editar"
                >
                  <i className="fas fa-pen" />
                </button>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );

  const renderTable = () => (
    <div className="admin-table-wrapper">
      <table className="admin-table">
        <thead>
          <tr>
            <th>Nombre</th>
            <th>Descripción</th>
            <th>Administradores</th>
            {isGeneralAdmin && <th>Editar</th>}
            {isGeneralAdmin && <th>Feature flags</th>}
            {isGeneralAdmin && <th>Consumos</th>}
            {isGeneralAdmin && <th>Estado</th>}
            <th></th>
          </tr>
        </thead>
        <tbody>
          {filteredRegionales.map((reg) => {
            const activo = Boolean(reg?.activo ?? reg?.Activo ?? true);
            return (
            <tr key={reg.id || reg.Id || reg.nombre} className={activo ? '' : 'row-inactive'}>
              <td>{reg.nombre || reg.name || 'Sin nombre'}</td>
              <td>{reg.descripcion || reg.detalle || '-'}</td>
              <td className="actions">
                <button
                  className="btn btn-secondary"
                  onClick={() => (reg.id || reg.Id) && navigate(`/admin/regionales/${reg.id || reg.Id}/admins/nuevo`)}
                  type="button"
                  disabled={!reg.id && !reg.Id}
                  title="Administradores"
                >
                  <i className="fas fa-user-plus" />
                </button>
              </td>
              {isGeneralAdmin && (
                <td className="actions">
                  <button
                    className="btn btn-secondary"
                    onClick={() => (reg.id || reg.Id) && navigate(`/admin/regionales/${reg.id || reg.Id}/editar`)}
                    type="button"
                    disabled={!reg.id && !reg.Id}
                    title="Editar"
                  >
                    <i className="fas fa-pen" />
                  </button>
                </td>
              )}
              {isGeneralAdmin && (
                <td className="actions">
                  <button
                    className="btn btn-secondary"
                    onClick={() => openFeatureFlags(reg)}
                    type="button"
                    disabled={!reg.id && !reg.Id}
                    title="Feature flags"
                  >
                    <i className="fas fa-toggle-on" />
                  </button>
                </td>
              )}
              {isGeneralAdmin && (
                <td className="actions">
                  <button
                    className="btn btn-secondary"
                    onClick={() => openConsumos(reg)}
                    type="button"
                    disabled={!reg.id && !reg.Id}
                    title="Consumos"
                  >
                    <i className="fas fa-chart-bar" />
                  </button>
                </td>
              )}
              {isGeneralAdmin && (
                <td className="actions">
                  <button
                    className={`btn ${activo ? 'btn-danger' : 'btn-success'}`}
                    onClick={() => handleToggleRegionalActivo(reg)}
                    type="button"
                    disabled={!reg.id && !reg.Id}
                    title={activo ? 'Desactivar regional' : 'Activar regional'}
                  >
                    <i className={`fas ${activo ? 'fa-ban' : 'fa-check'}`} />
                  </button>
                </td>
              )}
              <td className="actions">
                <button className="btn btn-primary" onClick={() => handleSelect(reg)} type="button" title="Administrar">
                  Administrar
                  <i className="fas fa-arrow-right" />
                </button>
              </td>
            </tr>
          );
          })}
        </tbody>
      </table>
    </div>
  );

  return (
    <div className="admin-select-regional">
      {isSelectingRegional && (
        <LoadingSpinner message={`Accediendo a la regional ${selectingRegionalName}`} />
      )}
      <header className="page-header">
        <div>
          <p className="eyebrow">Modo administrador</p>
          <h1>Seleccioná la regional/rectorado para trabajar</h1>
          <p className="subtitle">Debes elegir un regional antes de acceder al dashboard de administración.</p>
        </div>
        <div className="header-actions">
          <ActiveRegionalBadge />
          <ProcesosMenu />
          <button
            type="button"
            className="logout-btn-header"
            onClick={logout}
            title="Cerrar sesión"
          >
            <i className="fas fa-sign-out-alt" />
            <span>Salir</span>
          </button>
        </div>
      </header>

      <div className="toolbar">
        <div className="search-box">
          <i className="fas fa-search" />
          <input
            type="text"
            placeholder="Buscar regional..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="toolbar-actions">
          {isGeneralAdmin && (
            <button
              className="new-regional-btn"
              onClick={() => navigate('/admin/regionales/nuevo')}
              type="button"
            >
              <i className="fas fa-plus" /> Alta de regional
            </button>
          )}
          <div className="view-toggle">
            <button
              className={viewMode === 'cards' ? 'active' : ''}
              onClick={() => setViewMode('cards')}
              type="button"
            >
              <i className="fas fa-th-large" /> Tarjetas
            </button>
            <button
              className={viewMode === 'table' ? 'active' : ''}
              onClick={() => setViewMode('table')}
              type="button"
            >
              <i className="fas fa-table" /> Tabla
            </button>
          </div>
        </div>
      </div>

      {isLoading && <div className="state">Cargando regionales...</div>}
      {error && <div className="state error">{error}</div>}
      {actionMessage.text && (
        <div className={`state ${actionMessage.type}`}>{actionMessage.text}</div>
      )}

      {!isLoading && !error && regionales.length === 0 && (
        <div className="state">No hay regionales disponibles.</div>
      )}

      {!isLoading && !error && regionales.length > 0 && (
        viewMode === 'table' ? renderTable() : renderCards()
      )}

      {ffModal.open && (
        <div className="ff-modal-overlay" onClick={closeFeatureFlags}>
          <div className="ff-modal" onClick={(e) => e.stopPropagation()}>
            <div className="ff-modal-header">
              <div>
                <p className="ff-modal-eyebrow">Feature flags</p>
                <h3>{ffModal.regional?.nombre || ffModal.regional?.Nombre || 'Regional'}</h3>
              </div>
              <button className="ff-modal-close" onClick={closeFeatureFlags} type="button" title="Cerrar">
                <i className="fas fa-times" />
              </button>
            </div>

            {ffModal.loading && (
              <div className="ff-modal-state">
                <i className="fas fa-spinner fa-spin" />
                <span>Cargando feature flags...</span>
              </div>
            )}

            {!ffModal.loading && ffModal.error && (
              <div className="ff-modal-error">{ffModal.error}</div>
            )}

            {!ffModal.loading && !ffModal.error && (
              <div className="ff-modal-list">
                {ffModal.items.length === 0 && (
                  <div className="ff-empty">No hay feature flags cargadas.</div>
                )}
                {ffModal.items.map((flag) => {
                  const nombre = flag?.nombre || flag?.Nombre || '';
                  const descripcion = flag?.descripcion || flag?.Descripcion || '';
                  const activo = Boolean(flag?.activo ?? flag?.Activo);
                  return (
                    <div key={nombre} className="ff-item">
                      <div className="ff-info">
                        <h4>{nombre}</h4>
                        <p>{descripcion || 'Sin descripción'}</p>
                      </div>
                      <label className="ff-toggle">
                        <input
                          type="checkbox"
                          checked={activo}
                          onChange={() => handleToggleFeatureFlag(flag)}
                        />
                        <span className="ff-slider" />
                      </label>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {consumosModal.open && (
        <div className="ff-modal-overlay" onClick={closeConsumos}>
          <div className="ff-modal consumos-modal" onClick={(e) => e.stopPropagation()}>
            <div className="ff-modal-header">
              <div>
                <p className="ff-modal-eyebrow">Consumos</p>
                <h3>{consumosModal.regional?.nombre || consumosModal.regional?.Nombre || 'Regional'}</h3>
              </div>
              <button className="ff-modal-close" onClick={closeConsumos} type="button" title="Cerrar">
                <i className="fas fa-times" />
              </button>
            </div>

            {consumosModal.loading && (
              <div className="ff-modal-state">
                <i className="fas fa-spinner fa-spin" />
                <span>Cargando consumos...</span>
              </div>
            )}

            {!consumosModal.loading && consumosModal.error && (
              <div className="ff-modal-error">{consumosModal.error}</div>
            )}

            {!consumosModal.loading && !consumosModal.error && consumosModal.data && (
              <div className="consumos-content">
                <div className="consumos-summary">
                  <p className="consumos-label">Tokens consumidos en el mes en curso</p>
                  <div className="consumos-total">
                    {(consumosModal.data?.mesActual?.totalTokens ?? consumosModal.data?.mesActual?.TotalTokens) ?? 0}
                  </div>
                  <div className="consumos-breakdown">
                    <span>
                      Enviados: {(consumosModal.data?.mesActual?.enviadosTokens ?? consumosModal.data?.mesActual?.EnviadosTokens) ?? 0}
                    </span>
                    <span>
                      Recibidos: {(consumosModal.data?.mesActual?.recibidosTokens ?? consumosModal.data?.mesActual?.RecibidosTokens) ?? 0}
                    </span>
                  </div>
                </div>

                <div className="consumos-chart">
                  <div className="consumos-chart-header">
                    <h4>Consumo mensual ({consumosModal.data?.year || new Date().getFullYear()})</h4>
                  </div>
                  <div className="consumos-chart-bars">
                    {(consumosModal.data?.meses || []).map((item) => {
                      const total = item.totalTokens ?? item.TotalTokens ?? 0;
                      const enviados = item.enviadosTokens ?? item.EnviadosTokens ?? 0;
                      const recibidos = item.recibidosTokens ?? item.RecibidosTokens ?? 0;
                      const max = (consumosModal.data?.maxTokens ?? consumosModal.data?.MaxTokens) || 1;
                      const heightPct = Math.min(100, Math.round((total / max) * 100));
                      const enviadosPct = total > 0 ? Math.round((enviados / total) * 100) : 0;
                      const mes = item.mes ?? item.Mes;
                      const mesLabel = item.mesLabel ?? item.MesLabel ?? mes;
                      return (
                        <div key={mes} className="consumos-bar">
                          <div className="consumos-bar-track">
                            <div className="consumos-bar-fill" style={{ height: `${heightPct}%` }}>
                              <div className="consumos-bar-part enviados" style={{ height: `${enviadosPct}%` }} />
                              <div className="consumos-bar-part recibidos" style={{ height: `${100 - enviadosPct}%` }} />
                            </div>
                          </div>
                          <span className="consumos-bar-label">{mesLabel}</span>
                          <span className="consumos-bar-value">{total}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {confirmModal.open && (
        <div className="confirm-modal-overlay" onClick={closeConfirmModal}>
          <div className="confirm-modal" onClick={(e) => e.stopPropagation()}>
            <div className="confirm-modal-header">
              <div>
                <p className="confirm-modal-eyebrow">Regionales</p>
                <h3>
                  {confirmModal.nextActivo ? 'Activar regional' : 'Desactivar regional'}
                </h3>
              </div>
              <button
                className="confirm-modal-close"
                onClick={closeConfirmModal}
                type="button"
                title="Cerrar"
                disabled={confirmModal.loading}
              >
                <i className="fas fa-times" />
              </button>
            </div>
            <p className="confirm-modal-text">
              {confirmModal.nextActivo
                ? `Vas a activar el regional "${confirmModal.regional?.nombre || confirmModal.regional?.name || 'sin nombre'}".`
                : `Vas a desactivar el regional "${confirmModal.regional?.nombre || confirmModal.regional?.name || 'sin nombre'}".`}
            </p>
            <div className="confirm-modal-actions">
              <button
                className="btn btn-secondary"
                type="button"
                onClick={closeConfirmModal}
                disabled={confirmModal.loading}
              >
                Cancelar
              </button>
              <button
                className={`btn ${confirmModal.nextActivo ? 'btn-success' : 'btn-danger'}`}
                type="button"
                onClick={confirmToggleRegional}
                disabled={confirmModal.loading}
              >
                {confirmModal.loading ? 'Procesando...' : confirmModal.nextActivo ? 'Activar' : 'Desactivar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminSelectRegional;
